import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Wallet,
  Check,
  X,
  CalendarDays,
  Filter,
  IndianRupee,
} from "lucide-react";

import api from "../constant/api";
import { toast } from "react-toastify";
import { useMenuPermission } from "../hooks/useMenuPermission";

const MENU_ID = 22;

const CATEGORIES = [
  "travel",
  "food",
  "accommodation",
  "fuel",
  "misc",
];

const FILTERS = [
  {
    value: "all",
    label: "All",
  },
  {
    value: "daily",
    label: "Daily",
  },
  {
    value: "monthly",
    label: "Monthly",
  },
  {
    value: "yearly",
    label: "Yearly",
  },
];

const getToday = () => {
  return new Date().toISOString().slice(0, 10);
};

const getCurrentMonth = () => {
  const now = new Date();

  return `${now.getFullYear()}-${String(
    now.getMonth() + 1
  ).padStart(2, "0")}`;
};

const getCurrentYear = () => {
  return String(new Date().getFullYear());
};

export default function Expenses() {
  const { loading } = useMenuPermission(MENU_ID);

  // =====================================================
  // USER
  // =====================================================

  const user = JSON.parse(
    localStorage.getItem("user") || "{}"
  );

  const user_id = user.user_id;
  const role = user.role;

  const isAdmin =
    role === "admin" ||
    role === "superadmin";

  // =====================================================
  // STATES
  // =====================================================

  const [expenses, setExpenses] = useState([]);

  const [showAdd, setShowAdd] =
    useState(false);

  const [busy, setBusy] =
    useState(false);

  const [loadingExpenses, setLoadingExpenses] =
    useState(false);

  // =====================================================
  // FILTER
  // =====================================================

  const [filterType, setFilterType] =
    useState("monthly");

  const [selectedDate, setSelectedDate] =
    useState(getToday());

  const [selectedMonth, setSelectedMonth] =
    useState(getCurrentMonth());

  const [selectedYear, setSelectedYear] =
    useState(getCurrentYear());

  // =====================================================
  // FORM
  // =====================================================

  const [form, setForm] = useState({
    category: "travel",
    amount: "",
    expense_date: getToday(),
    description: "",
  });

  // =====================================================
  // LOAD EXPENSES
  // =====================================================

  const load = async () => {
    if (
      user_id === undefined ||
      user_id === null ||
      user_id === ""
    ) {
      toast.error("User ID not found");
      return;
    }

    try {
      setLoadingExpenses(true);

      const res = await api.get(
        `/Salesman/getExpenses?user_id=${encodeURIComponent(
          user_id
        )}`
      );

      console.log(
        "GET EXPENSES RESPONSE:",
        res.data
      );

      if (res.data?.success) {
        setExpenses(
          Array.isArray(res.data.data)
            ? res.data.data
            : []
        );
      } else {
        setExpenses([]);

        toast.error(
          res.data?.msg ||
            "Failed to load expenses"
        );
      }
    } catch (error) {
      console.log(
        "Load Expenses Error:",
        error?.response?.data ||
          error
      );

      toast.error(
        error?.response?.data?.msg ||
          "Failed to load expenses"
      );
    } finally {
      setLoadingExpenses(false);
    }
  };

  // =====================================================
  // INITIAL LOAD
  // =====================================================

  useEffect(() => {
    if (user_id) {
      load();
    }
  }, [user_id, role]);

  // =====================================================
  // FORM CHANGE
  // =====================================================

  const handleFormChange = (
    field,
    value
  ) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // =====================================================
  // RESET FORM
  // =====================================================

  const resetForm = () => {
    setForm({
      category: "travel",
      amount: "",
      expense_date: getToday(),
      description: "",
    });
  };

  // =====================================================
  // ADD EXPENSE
  // =====================================================

  const handleAdd = async () => {
    const amount = Number(form.amount);

    if (!form.amount) {
      toast.error("Enter an amount");
      return;
    }

    if (
      !Number.isFinite(amount) ||
      amount <= 0
    ) {
      toast.error(
        "Enter a valid amount"
      );
      return;
    }

    if (!form.expense_date) {
      toast.error(
        "Select expense date"
      );
      return;
    }

    if (
      user_id === undefined ||
      user_id === null ||
      user_id === ""
    ) {
      toast.error(
        "User ID not found"
      );
      return;
    }

    try {
      setBusy(true);

      const payload = {
        user_id: Number(user_id),

        category:
          form.category || "misc",

        amount: amount,

        expense_date:
          form.expense_date,

        description:
          form.description?.trim() ||
          "",
      };

      console.log(
        "ADD EXPENSE PAYLOAD:",
        payload
      );

      const res = await api.post(
        "/Salesman/addExpense",
        payload
      );

      console.log(
        "ADD EXPENSE RESPONSE:",
        res.data
      );

      if (res.data?.success) {
        toast.success(
          "Expense submitted successfully ✅"
        );

        setShowAdd(false);

        resetForm();

        await load();
      } else {
        toast.error(
          res.data?.msg ||
            "Failed to submit expense"
        );
      }
    } catch (error) {
      console.log(
        "ADD EXPENSE ERROR:",
        error?.response?.data ||
          error
      );

      toast.error(
        error?.response?.data?.msg ||
          "Failed to submit expense"
      );
    } finally {
      setBusy(false);
    }
  };

  // =====================================================
  // APPROVE / REJECT
  // =====================================================

  const handleDecision = async (
    expense_id,
    status
  ) => {
    if (
      expense_id === undefined ||
      expense_id === null ||
      expense_id === ""
    ) {
      toast.error(
        "Expense ID not found"
      );
      return;
    }

    if (
      user_id === undefined ||
      user_id === null ||
      user_id === ""
    ) {
      toast.error(
        "User ID not found"
      );
      return;
    }

    try {
      const res = await api.put(
        `/Salesman/decideExpense/${expense_id}`,
        {
          user_id: Number(user_id),
          status,
        }
      );

      console.log(
        "Decision Response:",
        res.data
      );

      if (res.data?.success) {
        toast.success(
          status === "approved"
            ? "Expense approved ✅"
            : "Expense rejected ❌"
        );

        await load();
      } else {
        toast.error(
          res.data?.msg ||
            "Failed to update expense"
        );
      }
    } catch (error) {
      console.log(
        "Decision Error:",
        error?.response?.data ||
          error
      );

      toast.error(
        error?.response?.data?.msg ||
          "Failed to update expense"
      );
    }
  };

  // =====================================================
  // STATUS COLOR
  // =====================================================

  const statusColor = (status) => {
    if (status === "approved") {
      return "bg-green-100 text-green-700";
    }

    if (status === "rejected") {
      return "bg-red-100 text-red-700";
    }

    return "bg-amber-100 text-amber-700";
  };

  // =====================================================
  // DATE FORMAT
  // =====================================================

  const formatDate = (date) => {
    if (!date) {
      return "—";
    }

    const parsed = new Date(date);

    if (
      Number.isNaN(
        parsed.getTime()
      )
    ) {
      return "—";
    }

    return parsed.toLocaleDateString(
      "en-IN"
    );
  };

  // =====================================================
  // NORMALIZE DATE
  // =====================================================

  const getDateString = (date) => {
    if (!date) {
      return "";
    }

    const d = new Date(date);

    if (
      Number.isNaN(
        d.getTime()
      )
    ) {
      return "";
    }

    const year =
      d.getFullYear();

    const month = String(
      d.getMonth() + 1
    ).padStart(2, "0");

    const day = String(
      d.getDate()
    ).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  // =====================================================
  // FILTER EXPENSES
  // =====================================================

  const filteredExpenses =
    expenses.filter((expense) => {
      const expenseDate =
        getDateString(
          expense.expense_date
        );

      if (!expenseDate) {
        return false;
      }

      // -----------------------------------------------
      // ALL
      // -----------------------------------------------

      if (filterType === "all") {
        return true;
      }

      // -----------------------------------------------
      // DAILY
      // -----------------------------------------------

      if (filterType === "daily") {
        return (
          expenseDate ===
          selectedDate
        );
      }

      // -----------------------------------------------
      // MONTHLY
      // -----------------------------------------------

      if (filterType === "monthly") {
        return expenseDate.startsWith(
          selectedMonth
        );
      }

      // -----------------------------------------------
      // YEARLY
      // -----------------------------------------------

      if (filterType === "yearly") {
        return expenseDate.startsWith(
          selectedYear
        );
      }

      return true;
    });

  // =====================================================
  // TOTAL
  // =====================================================

  const totalAmount =
    filteredExpenses.reduce(
      (total, expense) => {
        return (
          total +
          Number(
            expense.amount || 0
          )
        );
      },
      0
    );

  // =====================================================
  // APPROVED TOTAL
  // =====================================================

  const approvedAmount =
    filteredExpenses
      .filter(
        (expense) =>
          expense.status ===
          "approved"
      )
      .reduce(
        (total, expense) => {
          return (
            total +
            Number(
              expense.amount || 0
            )
          );
        },
        0
      );

  // =====================================================
  // PENDING TOTAL
  // =====================================================

  const pendingAmount =
    filteredExpenses
      .filter(
        (expense) =>
          expense.status ===
          "pending"
      )
      .reduce(
        (total, expense) => {
          return (
            total +
            Number(
              expense.amount || 0
            )
          );
        },
        0
      );

  // =====================================================
  // REJECTED TOTAL
  // =====================================================

  const rejectedAmount =
    filteredExpenses
      .filter(
        (expense) =>
          expense.status ===
          "rejected"
      )
      .reduce(
        (total, expense) => {
          return (
            total +
            Number(
              expense.amount || 0
            )
          );
        },
        0
      );

  // =====================================================
  // PERMISSION LOADING
  // =====================================================

  if (loading) {
    return (
      <div className="p-6 text-gray-500">
        Checking permissions...
      </div>
    );
  }

  // =====================================================
  // UI
  // =====================================================

  return (
    <div className="w-full">

      {/* =================================================
          HEADER
      ================================================= */}

      <div className="
        flex
        flex-col
        sm:flex-row
        sm:items-center
        sm:justify-between
        gap-3
        mb-5
      ">

        <div>
          <h1 className="
            text-2xl
            md:text-3xl
            font-bold
            text-gray-800
          ">
            Expenses
          </h1>

          <p className="
            text-sm
            text-gray-500
            mt-1
          ">
            {isAdmin
              ? "Review and approve team expenses"
              : "Submit your field expenses"}
          </p>
        </div>

        <button
          onClick={() =>
            setShowAdd(true)
          }
          className="
            hidden
            md:flex
            items-center
            gap-2
            bg-blue-600
            hover:bg-blue-700
            text-white
            px-5
            py-2.5
            rounded-xl
            shadow
            transition
          "
        >
          <Plus size={16} />
          Add Expense
        </button>

      </div>

      {/* =================================================
          FILTER SECTION
      ================================================= */}

      <div className="
        bg-white
        border
        border-gray-100
        rounded-2xl
        shadow-sm
        p-4
        mb-5
      ">

        <div className="
          flex
          items-center
          gap-2
          mb-3
        ">
          <Filter
            size={17}
            className="text-blue-600"
          />

          <h3 className="
            font-semibold
            text-gray-800
          ">
            Expense Filter
          </h3>
        </div>

        {/* FILTER BUTTONS */}

        <div className="
          grid
          grid-cols-2
          md:flex
          gap-2
          mb-4
        ">

          {FILTERS.map(
            (filter) => (
              <button
                key={filter.value}
                onClick={() =>
                  setFilterType(
                    filter.value
                  )
                }
                className={`
                  px-4
                  py-2.5
                  rounded-xl
                  text-sm
                  font-semibold
                  transition
                  ${
                    filterType ===
                    filter.value
                      ? "bg-blue-600 text-white shadow"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }
                `}
              >
                {filter.label}
              </button>
            )
          )}

        </div>

        {/* DAILY DATE */}

        {filterType ===
          "daily" && (
          <div className="
            max-w-xs
          ">

            <label className="
              block
              text-xs
              font-semibold
              text-gray-500
              mb-1
            ">
              Select Date
            </label>

            <div className="
              relative
            ">

              <CalendarDays
                size={17}
                className="
                  absolute
                  left-3
                  top-1/2
                  -translate-y-1/2
                  text-gray-400
                "
              />

              <input
                type="date"
                value={
                  selectedDate
                }
                onChange={(e) =>
                  setSelectedDate(
                    e.target.value
                  )
                }
                className="
                  border
                  w-full
                  pl-10
                  pr-3
                  py-2.5
                  rounded-xl
                  outline-none
                  focus:ring-2
                  focus:ring-blue-500
                "
              />

            </div>

          </div>
        )}

        {/* MONTHLY */}

        {filterType ===
          "monthly" && (
          <div className="
            max-w-xs
          ">

            <label className="
              block
              text-xs
              font-semibold
              text-gray-500
              mb-1
            ">
              Select Month
            </label>

            <div className="
              relative
            ">

              <CalendarDays
                size={17}
                className="
                  absolute
                  left-3
                  top-1/2
                  -translate-y-1/2
                  text-gray-400
                "
              />

              <input
                type="month"
                value={
                  selectedMonth
                }
                onChange={(e) =>
                  setSelectedMonth(
                    e.target.value
                  )
                }
                className="
                  border
                  w-full
                  pl-10
                  pr-3
                  py-2.5
                  rounded-xl
                  outline-none
                  focus:ring-2
                  focus:ring-blue-500
                "
              />

            </div>

          </div>
        )}

        {/* YEARLY */}

        {filterType ===
          "yearly" && (
          <div className="
            max-w-xs
          ">

            <label className="
              block
              text-xs
              font-semibold
              text-gray-500
              mb-1
            ">
              Select Year
            </label>

            <div className="
              relative
            ">

              <CalendarDays
                size={17}
                className="
                  absolute
                  left-3
                  top-1/2
                  -translate-y-1/2
                  text-gray-400
                "
              />

              <select
                value={
                  selectedYear
                }
                onChange={(e) =>
                  setSelectedYear(
                    e.target.value
                  )
                }
                className="
                  border
                  w-full
                  pl-10
                  pr-3
                  py-2.5
                  rounded-xl
                  outline-none
                  focus:ring-2
                  focus:ring-blue-500
                "
              >

                {Array.from(
                  {
                    length: 6,
                  },
                  (_, index) =>
                    String(
                      new Date().getFullYear() -
                        index
                    )
                ).map(
                  (year) => (
                    <option
                      key={year}
                      value={year}
                    >
                      {year}
                    </option>
                  )
                )}

              </select>

            </div>

          </div>
        )}

      </div>

      {/* =================================================
          SUMMARY CARDS
      ================================================= */}

      <div className="
        grid
        grid-cols-2
        lg:grid-cols-4
        gap-3
        mb-5
      ">

        {/* TOTAL */}

        <div className="
          bg-white
          border
          border-gray-100
          rounded-2xl
          shadow-sm
          p-4
        ">

          <div className="
            flex
            items-center
            justify-between
          ">

            <div>

              <p className="
                text-xs
                text-gray-500
              ">
                Total Expense
              </p>

              <p className="
                text-lg
                md:text-xl
                font-bold
                text-blue-600
                mt-1
              ">
                ₹
                {totalAmount.toLocaleString(
                  "en-IN"
                )}
              </p>

            </div>

            <div className="
              w-9
              h-9
              rounded-xl
              bg-blue-100
              flex
              items-center
              justify-center
            ">
              <IndianRupee
                size={18}
                className="text-blue-600"
              />
            </div>

          </div>

        </div>

        {/* APPROVED */}

        <div className="
          bg-white
          border
          border-gray-100
          rounded-2xl
          shadow-sm
          p-4
        ">

          <p className="
            text-xs
            text-gray-500
          ">
            Approved
          </p>

          <p className="
            text-lg
            md:text-xl
            font-bold
            text-green-600
            mt-1
          ">
            ₹
            {approvedAmount.toLocaleString(
              "en-IN"
            )}
          </p>

        </div>

        {/* PENDING */}

        <div className="
          bg-white
          border
          border-gray-100
          rounded-2xl
          shadow-sm
          p-4
        ">

          <p className="
            text-xs
            text-gray-500
          ">
            Pending
          </p>

          <p className="
            text-lg
            md:text-xl
            font-bold
            text-amber-600
            mt-1
          ">
            ₹
            {pendingAmount.toLocaleString(
              "en-IN"
            )}
          </p>

        </div>

        {/* REJECTED */}

        <div className="
          bg-white
          border
          border-gray-100
          rounded-2xl
          shadow-sm
          p-4
        ">

          <p className="
            text-xs
            text-gray-500
          ">
            Rejected
          </p>

          <p className="
            text-lg
            md:text-xl
            font-bold
            text-red-600
            mt-1
          ">
            ₹
            {rejectedAmount.toLocaleString(
              "en-IN"
            )}
          </p>

        </div>

      </div>

      {/* =================================================
          FILTER RESULT
      ================================================= */}

      <div className="
        flex
        items-center
        justify-between
        mb-3
      ">

        <p className="
          text-sm
          text-gray-500
        ">
          Showing{" "}
          <span className="
            font-semibold
            text-gray-800
          ">
            {filteredExpenses.length}
          </span>{" "}
          expenses
        </p>

        {loadingExpenses && (
          <p className="
            text-xs
            text-gray-400
          ">
            Loading...
          </p>
        )}

      </div>

      {/* =================================================
          DESKTOP TABLE
      ================================================= */}

      <div className="
        hidden
        md:block
        overflow-x-auto
        bg-white
        rounded-2xl
        shadow
        border
        border-gray-100
      ">

        <table className="
          min-w-full
          text-sm
        ">

          <thead className="
            bg-gradient-to-r
            from-gray-900
            to-gray-700
            text-white
          ">

            <tr>

              {isAdmin && (
                <th className="
                  px-4
                  py-3
                  text-left
                ">
                  Salesman
                </th>
              )}

              <th className="
                px-4
                py-3
                text-left
              ">
                Category
              </th>

              <th className="
                px-4
                py-3
                text-left
              ">
                Description
              </th>

              <th className="
                px-4
                py-3
                text-left
              ">
                Date
              </th>

              <th className="
                px-4
                py-3
                text-right
              ">
                Amount
              </th>

              <th className="
                px-4
                py-3
                text-center
              ">
                Status
              </th>

              {isAdmin && (
                <th className="
                  px-4
                  py-3
                  text-center
                ">
                  Action
                </th>
              )}

            </tr>

          </thead>

          <tbody>

            {filteredExpenses.map(
              (expense) => (
                <tr
                  key={
                    expense.expense_id
                  }
                  className="
                    border-b
                    hover:bg-gray-50
                    transition
                  "
                >

                  {/* SALESMAN */}

                  {isAdmin && (
                    <td className="
                      px-4
                      py-3
                    ">

                      <div>

                        <p className="
                          font-medium
                          text-gray-800
                        ">
                          {expense.salesman_name ||
                            "Unknown"}
                        </p>

                        <p className="
                          text-xs
                          text-gray-400
                        ">
                          User ID:{" "}
                          {expense.user_id}
                        </p>

                      </div>

                    </td>
                  )}

                  {/* CATEGORY */}

                  <td className="
                    px-4
                    py-3
                    capitalize
                  ">
                    {expense.category ||
                      "misc"}
                  </td>

                  {/* DESCRIPTION */}

                  <td className="
                    px-4
                    py-3
                    text-gray-600
                  ">
                    {expense.description ||
                      "—"}
                  </td>

                  {/* DATE */}

                  <td className="
                    px-4
                    py-3
                  ">
                    {formatDate(
                      expense.expense_date
                    )}
                  </td>

                  {/* AMOUNT */}

                  <td className="
                    px-4
                    py-3
                    text-right
                    font-semibold
                  ">
                    ₹
                    {Number(
                      expense.amount || 0
                    ).toLocaleString(
                      "en-IN"
                    )}
                  </td>

                  {/* STATUS */}

                  <td className="
                    px-4
                    py-3
                    text-center
                  ">

                    <span
                      className={`
                        px-2
                        py-1
                        rounded-full
                        text-xs
                        font-semibold
                        capitalize
                        ${statusColor(
                          expense.status
                        )}
                      `}
                    >
                      {expense.status ||
                        "pending"}
                    </span>

                  </td>

                  {/* ACTION */}

                  {isAdmin && (
                    <td className="
                      px-4
                      py-3
                      text-center
                    ">

                      {expense.status ===
                      "pending" ? (

                        <div className="
                          flex
                          justify-center
                          gap-2
                        ">

                          <button
                            onClick={() =>
                              handleDecision(
                                expense.expense_id,
                                "approved"
                              )
                            }
                            className="
                              bg-green-500
                              hover:bg-green-600
                              text-white
                              p-2
                              rounded-lg
                              transition
                            "
                            title="Approve"
                          >
                            <Check
                              size={14}
                            />
                          </button>

                          <button
                            onClick={() =>
                              handleDecision(
                                expense.expense_id,
                                "rejected"
                              )
                            }
                            className="
                              bg-red-500
                              hover:bg-red-600
                              text-white
                              p-2
                              rounded-lg
                              transition
                            "
                            title="Reject"
                          >
                            <X
                              size={14}
                            />
                          </button>

                        </div>

                      ) : (
                        "—"
                      )}

                    </td>
                  )}

                </tr>
              )
            )}

            {/* EMPTY */}

            {filteredExpenses.length ===
              0 && (
              <tr>

                <td
                  colSpan={
                    isAdmin
                      ? 7
                      : 5
                  }
                  className="
                    text-center
                    py-12
                    text-gray-400
                  "
                >

                  <Wallet
                    className="
                      mx-auto
                      mb-2
                    "
                    size={30}
                  />

                  No expenses found
                  for this filter.

                </td>

              </tr>
            )}

          </tbody>

        </table>

      </div>

      {/* =================================================
          MOBILE CARDS
      ================================================= */}

      <div className="
        md:hidden
        space-y-3
        pb-24
      ">

        {filteredExpenses.map(
          (expense) => (
            <motion.div
              key={
                expense.expense_id
              }
              initial={{
                opacity: 0,
                y: 15,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              className="
                bg-white
                border
                border-gray-100
                rounded-2xl
                shadow-sm
                p-4
              "
            >

              <div className="
                flex
                justify-between
                items-start
              ">

                <div>

                  <p className="
                    font-semibold
                    text-gray-800
                    text-sm
                    capitalize
                  ">
                    {expense.category ||
                      "misc"}
                  </p>

                  {isAdmin && (
                    <p className="
                      text-xs
                      text-gray-400
                      mt-1
                    ">
                      {expense.salesman_name ||
                        "Unknown"}
                    </p>
                  )}

                  <p className="
                    text-xs
                    text-gray-400
                    mt-1
                  ">
                    {formatDate(
                      expense.expense_date
                    )}
                  </p>

                </div>

                <span
                  className={`
                    px-2
                    py-1
                    rounded-full
                    text-[10px]
                    font-semibold
                    capitalize
                    ${statusColor(
                      expense.status
                    )}
                  `}
                >
                  {expense.status ||
                    "pending"}
                </span>

              </div>

              {expense.description && (
                <p className="
                  text-xs
                  text-gray-500
                  mt-3
                ">
                  {expense.description}
                </p>
              )}

              <div className="
                flex
                items-center
                justify-between
                mt-4
              ">

                <p className="
                  text-lg
                  font-bold
                  text-blue-600
                ">
                  ₹
                  {Number(
                    expense.amount || 0
                  ).toLocaleString(
                    "en-IN"
                  )}
                </p>

                {isAdmin &&
                  expense.status ===
                    "pending" && (

                    <div className="
                      flex
                      gap-2
                    ">

                      <button
                        onClick={() =>
                          handleDecision(
                            expense.expense_id,
                            "approved"
                          )
                        }
                        className="
                          bg-green-500
                          hover:bg-green-600
                          text-white
                          p-2
                          rounded-lg
                        "
                      >
                        <Check
                          size={14}
                        />
                      </button>

                      <button
                        onClick={() =>
                          handleDecision(
                            expense.expense_id,
                            "rejected"
                          )
                        }
                        className="
                          bg-red-500
                          hover:bg-red-600
                          text-white
                          p-2
                          rounded-lg
                        "
                      >
                        <X
                          size={14}
                        />
                      </button>

                    </div>
                  )}

              </div>

            </motion.div>
          )
        )}

        {filteredExpenses.length ===
          0 && (
          <div className="
            text-center
            py-16
            text-gray-400
          ">

            <Wallet
              className="
                mx-auto
                mb-2
              "
              size={30}
            />

            No expenses found.

          </div>
        )}

      </div>

      {/* =================================================
          MOBILE ADD BUTTON
      ================================================= */}

      <button
        onClick={() =>
          setShowAdd(true)
        }
        className="
          fixed
          bottom-6
          right-5
          bg-blue-600
          hover:bg-blue-700
          text-white
          w-14
          h-14
          flex
          items-center
          justify-center
          rounded-full
          shadow-2xl
          md:hidden
          z-50
        "
      >
        <Plus size={20} />
      </button>

      {/* =================================================
          ADD EXPENSE MODAL
      ================================================= */}

      <AnimatePresence>

        {showAdd && (
          <>

            {/* BACKDROP */}

            <motion.div
              className="
                fixed
                inset-0
                bg-black/50
                z-[90]
              "
              initial={{
                opacity: 0,
              }}
              animate={{
                opacity: 1,
              }}
              exit={{
                opacity: 0,
              }}
              onClick={() =>
                !busy &&
                setShowAdd(false)
              }
            />

            {/* MODAL */}

            <motion.div
              className="
                fixed
                inset-x-0
                bottom-0
                sm:inset-0
                sm:flex
                sm:items-center
                sm:justify-center
                z-[100]
              "
              initial={{
                y: "100%",
              }}
              animate={{
                y: 0,
              }}
              exit={{
                y: "100%",
              }}
              transition={{
                type: "spring",
                damping: 25,
                stiffness: 250,
              }}
            >

              <div className="
                bg-white
                w-full
                sm:max-w-md
                sm:mx-4
                rounded-t-[28px]
                sm:rounded-2xl
                shadow-2xl
                max-h-[92vh]
                sm:max-h-[85vh]
                flex
                flex-col
                overflow-hidden
              ">

                {/* HANDLE */}

                <div className="
                  flex
                  justify-center
                  pt-3
                  sm:hidden
                ">
                  <div className="
                    w-14
                    h-1.5
                    bg-gray-300
                    rounded-full
                  " />
                </div>

                {/* HEADER */}

                <div className="
                  px-5
                  pt-3
                  sm:pt-5
                  pb-3
                  border-b
                  border-gray-100
                ">

                  <div className="
                    flex
                    items-center
                    justify-between
                  ">

                    <h2 className="
                      text-lg
                      font-bold
                      text-gray-800
                    ">
                      Add Expense
                    </h2>

                    <button
                      onClick={() =>
                        !busy &&
                        setShowAdd(false)
                      }
                      className="
                        w-8
                        h-8
                        rounded-full
                        bg-gray-100
                        flex
                        items-center
                        justify-center
                      "
                    >
                      <X size={16} />
                    </button>

                  </div>

                </div>

                {/* FORM */}

                <div className="
                  p-5
                  space-y-3
                  overflow-y-auto
                  flex-1
                ">

                  {/* CATEGORY */}

                  <div>

                    <label className="
                      text-xs
                      font-semibold
                      text-gray-500
                      mb-1
                      block
                    ">
                      Category
                    </label>

                    <select
                      className="
                        border
                        w-full
                        p-2.5
                        rounded-xl
                        outline-none
                        focus:ring-2
                        focus:ring-blue-500
                      "
                      value={
                        form.category
                      }
                      onChange={(e) =>
                        handleFormChange(
                          "category",
                          e.target.value
                        )
                      }
                    >

                      {CATEGORIES.map(
                        (category) => (
                          <option
                            key={
                              category
                            }
                            value={
                              category
                            }
                          >
                            {category
                              .charAt(
                                0
                              )
                              .toUpperCase() +
                              category.slice(
                                1
                              )}
                          </option>
                        )
                      )}

                    </select>

                  </div>

                  {/* AMOUNT */}

                  <div>

                    <label className="
                      text-xs
                      font-semibold
                      text-gray-500
                      mb-1
                      block
                    ">
                      Amount
                    </label>

                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Amount (₹)"
                      value={
                        form.amount
                      }
                      onChange={(e) =>
                        handleFormChange(
                          "amount",
                          e.target.value
                        )
                      }
                      className="
                        border
                        w-full
                        p-2.5
                        rounded-xl
                        outline-none
                        focus:ring-2
                        focus:ring-blue-500
                      "
                    />

                  </div>

                  {/* DATE */}

                  <div>

                    <label className="
                      text-xs
                      font-semibold
                      text-gray-500
                      mb-1
                      block
                    ">
                      Expense Date
                    </label>

                    <input
                      type="date"
                      value={
                        form.expense_date
                      }
                      onChange={(e) =>
                        handleFormChange(
                          "expense_date",
                          e.target.value
                        )
                      }
                      className="
                        border
                        w-full
                        p-2.5
                        rounded-xl
                        outline-none
                        focus:ring-2
                        focus:ring-blue-500
                      "
                    />

                  </div>

                  {/* DESCRIPTION */}

                  <div>

                    <label className="
                      text-xs
                      font-semibold
                      text-gray-500
                      mb-1
                      block
                    ">
                      Description
                    </label>

                    <textarea
                      rows={4}
                      placeholder="Description (optional)"
                      value={
                        form.description
                      }
                      onChange={(e) =>
                        handleFormChange(
                          "description",
                          e.target.value
                        )
                      }
                      className="
                        border
                        w-full
                        p-2.5
                        rounded-xl
                        outline-none
                        resize-none
                        focus:ring-2
                        focus:ring-blue-500
                      "
                    />

                  </div>

                </div>

                {/* FOOTER */}

                <div className="
                  flex
                  gap-2
                  p-4
                  border-t
                  border-gray-100
                  bg-white
                ">

                  <button
                    onClick={
                      handleAdd
                    }
                    disabled={busy}
                    className="
                      flex-1
                      bg-green-500
                      hover:bg-green-600
                      text-white
                      py-3
                      rounded-xl
                      font-semibold
                      active:scale-95
                      transition
                      disabled:opacity-50
                    "
                  >
                    {busy
                      ? "Submitting..."
                      : "Submit"}
                  </button>

                  <button
                    onClick={() =>
                      !busy &&
                      setShowAdd(false)
                    }
                    disabled={busy}
                    className="
                      flex-1
                      bg-gray-100
                      hover:bg-gray-200
                      text-gray-700
                      py-3
                      rounded-xl
                      font-semibold
                      active:scale-95
                      transition
                    "
                  >
                    Cancel
                  </button>

                </div>

              </div>

            </motion.div>

          </>
        )}

      </AnimatePresence>

    </div>
  );
}